import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Nav, Spinner, Table } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import {
  REVISION_CREATE_FROM_SOURCE,
  SOURCE_CONNECTION_CREATE,
  SOURCE_CONNECTION_DELETE,
  SOURCE_CONNECTION_ROTATE_SECRET,
  SOURCE_CONNECTION_TEST,
  SOURCE_CONNECTION_UPDATE,
  SOURCE_CONNECTION_VIEW,
} from '../auth/permissions';
import bakingApi from '../services/bakingApi';

async function sha256Hex(file) {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function StatusPill({ status }) {
  return <span className={`status-pill status-${status}`}>{status}</span>;
}

const emptyConnectionForm = {
  display_name: 'Production Perforce',
  server_address: '',
  username: '',
  authentication_method: 'ticket',
  credential: '',
  default_depot_path: '',
  default_stream: '',
  project_root: '',
  charset: 'utf8',
  ssl_fingerprint: '',
};

function ProjectRevisionsPanel({
  projectId,
  isMaya,
  revisions,
  onReload,
}) {
  const { hasTenantPermission } = useAuth();
  const [sourceTab, setSourceTab] = useState('upload');
  const [connections, setConnections] = useState([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connectionsError, setConnectionsError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showConnectionForm, setShowConnectionForm] = useState(false);
  const [connectionForm, setConnectionForm] = useState(emptyConnectionForm);
  const [connectionBusy, setConnectionBusy] = useState(false);
  const [connectionMessage, setConnectionMessage] = useState('');
  const [rotateCredential, setRotateCredential] = useState('');
  const [rotatingId, setRotatingId] = useState(null);
  const [revisionForm, setRevisionForm] = useState({
    connection_id: '',
    depot_path: '',
    project_root: '',
    revision_type: 'latest_submitted',
    changelist: '',
  });
  const [revisionBusy, setRevisionBusy] = useState(false);
  const [revisionMessage, setRevisionMessage] = useState('');
  const [revisionError, setRevisionError] = useState(null);

  const canViewConnections = hasTenantPermission(SOURCE_CONNECTION_VIEW);
  const canCreateConnection = hasTenantPermission(SOURCE_CONNECTION_CREATE);
  const canUpdateConnection = hasTenantPermission(SOURCE_CONNECTION_UPDATE);
  const canRotateSecret = hasTenantPermission(SOURCE_CONNECTION_ROTATE_SECRET);
  const canTestConnection = hasTenantPermission(SOURCE_CONNECTION_TEST);
  const canDeleteConnection = hasTenantPermission(SOURCE_CONNECTION_DELETE);
  const canCreateFromSource = hasTenantPermission(REVISION_CREATE_FROM_SOURCE);

  const loadConnections = useCallback(async () => {
    if (!canViewConnections) {
      setConnections([]);
      return;
    }
    setConnectionsLoading(true);
    setConnectionsError(null);
    try {
      const rows = await bakingApi.listSourceConnections(projectId);
      setConnections(rows);
      setRevisionForm((prev) => {
        if (prev.connection_id || !rows.length) return prev;
        const preferred =
          rows.find((c) => c.status === 'verified') || rows[0];
        return {
          ...prev,
          connection_id: preferred.id,
          depot_path: preferred.default_depot_path || '',
          project_root: preferred.project_root || '',
        };
      });
    } catch (err) {
      setConnectionsError(err.message || 'Failed to load source connections');
    } finally {
      setConnectionsLoading(false);
    }
  }, [canViewConnections, projectId]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const onUploadZip = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    setUploadStatus('Hashing archive…');
    try {
      const digest = await sha256Hex(file);
      setUploadStatus('Requesting upload authorization…');
      const auth = await bakingApi.initiateRevisionUpload(
        projectId,
        {
          original_filename: file.name,
          byte_size: file.size,
          content_sha256: digest,
          content_type: file.type || 'application/zip',
        },
        crypto.randomUUID()
      );
      setUploadStatus('Uploading to object storage…');
      const headers = { ...(auth.required_headers || {}) };
      const put = await fetch(auth.upload_url, {
        method: auth.upload_method || 'PUT',
        headers,
        body: file,
      });
      if (!put.ok) {
        throw new Error(`Direct upload failed (${put.status})`);
      }
      setUploadStatus('Confirming upload and validating…');
      const revision = await bakingApi.confirmRevisionUpload(auth.revision_id, {
        byte_size: file.size,
        content_sha256: digest,
      });
      setUploadStatus(`Revision ${revision.status}`);
      await onReload();
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
      setUploadStatus('');
    } finally {
      setUploading(false);
    }
  };

  const onCreateConnection = async (event) => {
    event.preventDefault();
    if (!canCreateConnection) return;
    setConnectionBusy(true);
    setConnectionMessage('');
    setConnectionsError(null);
    try {
      await bakingApi.createSourceConnection(projectId, {
        provider: 'perforce',
        display_name: connectionForm.display_name,
        server_address: connectionForm.server_address,
        username: connectionForm.username,
        authentication_method: connectionForm.authentication_method,
        credential: connectionForm.credential,
        default_depot_path: connectionForm.default_depot_path,
        default_stream: connectionForm.default_stream,
        project_root: connectionForm.project_root,
        workspace_strategy: 'managed_temporary',
        charset: connectionForm.charset || 'utf8',
        ssl_fingerprint: connectionForm.ssl_fingerprint,
      });
      setConnectionForm(emptyConnectionForm);
      setShowConnectionForm(false);
      setConnectionMessage('Connection created. Test it to verify credentials.');
      await loadConnections();
    } catch (err) {
      setConnectionsError(err.message || 'Failed to create connection');
    } finally {
      setConnectionBusy(false);
    }
  };

  const onTestConnection = async (connectionId) => {
    if (!canTestConnection) return;
    setConnectionBusy(true);
    setConnectionsError(null);
    try {
      const result = await bakingApi.testSourceConnection(projectId, connectionId);
      setConnectionMessage(
        `Verified as ${result.authenticated_user || 'user'} (${result.status}).`
      );
      await loadConnections();
    } catch (err) {
      setConnectionsError(err.message || 'Connection test failed');
      await loadConnections();
    } finally {
      setConnectionBusy(false);
    }
  };

  const onRotateSecret = async (connectionId) => {
    if (!canRotateSecret || !rotateCredential) return;
    setConnectionBusy(true);
    setConnectionsError(null);
    try {
      await bakingApi.rotateSourceConnectionSecret(projectId, connectionId, {
        credential: rotateCredential,
      });
      setRotateCredential('');
      setRotatingId(null);
      setConnectionMessage('Credential rotated. Re-test the connection.');
      await loadConnections();
    } catch (err) {
      setConnectionsError(err.message || 'Credential rotation failed');
    } finally {
      setConnectionBusy(false);
    }
  };

  const onDisableConnection = async (connectionId) => {
    if (!canUpdateConnection) return;
    setConnectionBusy(true);
    try {
      await bakingApi.disableSourceConnection(projectId, connectionId);
      await loadConnections();
    } catch (err) {
      setConnectionsError(err.message || 'Failed to disable connection');
    } finally {
      setConnectionBusy(false);
    }
  };

  const onDeleteConnection = async (connectionId) => {
    if (!canDeleteConnection) return;
    setConnectionBusy(true);
    try {
      await bakingApi.deleteSourceConnection(projectId, connectionId);
      await loadConnections();
    } catch (err) {
      setConnectionsError(err.message || 'Failed to delete connection');
    } finally {
      setConnectionBusy(false);
    }
  };

  const onCreatePerforceRevision = async (event) => {
    event.preventDefault();
    if (!canCreateFromSource) return;
    setRevisionBusy(true);
    setRevisionError(null);
    setRevisionMessage('');
    try {
      const revision =
        revisionForm.revision_type === 'changelist'
          ? { type: 'changelist', value: revisionForm.changelist }
          : { type: 'latest_submitted' };
      const result = await bakingApi.createRevisionFromPerforce(projectId, {
        connection_id: revisionForm.connection_id,
        depot_path: revisionForm.depot_path || undefined,
        project_root: revisionForm.project_root || '',
        clean_sync: true,
        revision,
      });
      setRevisionMessage(
        result.created
          ? `Revision queued (CL ${result.source_resolved_changelist}).`
          : `Existing revision reused (CL ${result.source_resolved_changelist}).`
      );
      await onReload();
      await loadConnections();
    } catch (err) {
      setRevisionError(err.message || 'Failed to create Perforce revision');
    } finally {
      setRevisionBusy(false);
    }
  };

  const verifiedConnections = connections.filter((c) => c.status === 'verified');

  return (
    <div className="product-card" data-testid="project-revisions-panel">
      <h2 className="h5">Project revisions</h2>

      {revisions.length === 0 ? (
        <p className="text-muted">No revisions yet.</p>
      ) : (
        <Table responsive className="product-table mb-3">
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>Source</th>
              <th>Details</th>
              <th>Detected</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {revisions.map((revision) => (
              <tr key={revision.id} data-testid={`revision-row-${revision.id}`}>
                <td className="mono">{revision.id.slice(0, 8)}…</td>
                <td>
                  <StatusPill status={revision.status} />
                  {revision.is_development_fixture && (
                    <span className="fixture-badge">development fixture</span>
                  )}
                </td>
                <td>
                  {revision.source_type === 'perforce' ? (
                    <span className="fixture-badge" data-testid="perforce-badge">
                      Perforce
                    </span>
                  ) : (
                    revision.source_type || 'archive'
                  )}
                </td>
                <td>
                  {revision.source_type === 'perforce' ? (
                    <>
                      <div className="mono">
                        CL {revision.source_resolved_changelist || '—'}
                      </div>
                      <div className="small text-muted mono">
                        {revision.source_depot_path || '—'}
                      </div>
                    </>
                  ) : (
                    revision.original_filename || '—'
                  )}
                </td>
                <td>
                  {isMaya
                    ? revision.project_version_detected ||
                      (revision.scene_discovery_summary &&
                        revision.scene_discovery_summary.workspace_root) ||
                      '—'
                    : revision.project_version_detected || '—'}
                  {revision.scene_discovery_summary
                    ? ` · ${revision.scene_discovery_summary.count || 0} scenes`
                    : ''}
                  {isMaya && revision.scene_discovery_summary
                    ? ` · ${revision.scene_discovery_summary.ma_count || 0} .ma / ${
                        revision.scene_discovery_summary.mb_count || 0
                      } .mb`
                    : ''}
                  {revision.validation_error_code
                    ? ` · ${revision.validation_error_code}`
                    : ''}
                </td>
                <td>{new Date(revision.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Nav variant="tabs" className="mb-3" data-testid="revision-source-tabs">
        <Nav.Item>
          <Nav.Link
            active={sourceTab === 'upload'}
            onClick={() => setSourceTab('upload')}
            data-testid="tab-upload-zip"
          >
            Upload ZIP
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={sourceTab === 'perforce'}
            onClick={() => setSourceTab('perforce')}
            data-testid="tab-perforce"
          >
            Perforce
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {sourceTab === 'upload' && (
        <div data-testid="upload-zip-panel">
          <Form.Group controlId="revision-zip">
            <Form.Label>
              {isMaya ? 'Upload Maya project ZIP' : 'Upload Unity project ZIP'}
            </Form.Label>
            <Form.Control
              type="file"
              accept=".zip,application/zip"
              disabled={uploading}
              onChange={onUploadZip}
              data-testid="revision-zip-input"
            />
          </Form.Group>
          {uploadStatus && <p className="text-muted mt-2 mb-0">{uploadStatus}</p>}
          {uploadError && <div className="error-state mt-2">{uploadError}</div>}
          <Alert variant="light" className="mt-3 mb-0 border">
            {isMaya ? (
              <>
                Archives upload directly to object storage and are inspected statically
                (workspace + scenes). No Autodesk Maya process runs during upload. Maya
                execution is not enabled in this version.
              </>
            ) : (
              <>
                Archives upload directly to object storage. Bake outputs are downloadable
                after a successful job. Application happens in the Unity Editor after
                preview, backup, and user confirmation — not from this browser.
              </>
            )}
          </Alert>
        </div>
      )}

      {sourceTab === 'perforce' && (
        <div data-testid="perforce-panel">
          <Alert variant="light" className="border">
            Connect a read-only Perforce account to create immutable project revisions
            from submitted changelists. Credentials are stored securely and are made
            available only to an authorized worker for the duration of an assigned
            operation.
          </Alert>

          {!canViewConnections && (
            <p className="text-muted">
              You do not have permission to view project source connections.
            </p>
          )}

          {canViewConnections && connectionsLoading && (
            <p className="text-muted">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading connections…
            </p>
          )}
          {connectionsError && <div className="error-state">{connectionsError}</div>}
          {connectionMessage && (
            <Alert variant="success" className="border">
              {connectionMessage}
            </Alert>
          )}

          {canViewConnections &&
            !connectionsLoading &&
            connections.length === 0 &&
            !showConnectionForm && (
              <div data-testid="perforce-empty-state">
                <h3 className="h6">Connect Perforce</h3>
                <p className="text-muted">
                  No source connection is configured for this project yet.
                </p>
                {canCreateConnection && (
                  <Button
                    variant="primary"
                    onClick={() => setShowConnectionForm(true)}
                    data-testid="configure-perforce"
                  >
                    Configure connection
                  </Button>
                )}
              </div>
            )}

          {showConnectionForm && canCreateConnection && (
            <Form
              className="mb-4"
              onSubmit={onCreateConnection}
              data-testid="perforce-connection-form"
              autoComplete="off"
            >
              <h3 className="h6">Configure connection</h3>
              <div className="row g-2">
                <div className="col-md-6">
                  <Form.Label>Display name</Form.Label>
                  <Form.Control
                    value={connectionForm.display_name}
                    onChange={(e) =>
                      setConnectionForm({ ...connectionForm, display_name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="col-md-6">
                  <Form.Label>Server</Form.Label>
                  <Form.Control
                    value={connectionForm.server_address}
                    onChange={(e) =>
                      setConnectionForm({
                        ...connectionForm,
                        server_address: e.target.value,
                      })
                    }
                    placeholder="ssl:perforce.example.com:1666"
                    required
                    data-testid="perforce-server"
                  />
                </div>
                <div className="col-md-4">
                  <Form.Label htmlFor="perforce-username">Username</Form.Label>
                  <Form.Control
                    id="perforce-username"
                    value={connectionForm.username}
                    onChange={(e) =>
                      setConnectionForm({ ...connectionForm, username: e.target.value })
                    }
                    required
                    data-testid="perforce-username"
                  />
                </div>
                <div className="col-md-4">
                  <Form.Label>Authentication</Form.Label>
                  <Form.Select
                    value={connectionForm.authentication_method}
                    onChange={(e) =>
                      setConnectionForm({
                        ...connectionForm,
                        authentication_method: e.target.value,
                      })
                    }
                  >
                    <option value="ticket">Ticket (preferred)</option>
                    <option value="password">Password</option>
                  </Form.Select>
                </div>
                <div className="col-md-4">
                  <Form.Label>Ticket or password</Form.Label>
                  <Form.Control
                    type="password"
                    value={connectionForm.credential}
                    onChange={(e) =>
                      setConnectionForm({ ...connectionForm, credential: e.target.value })
                    }
                    required
                    autoComplete="new-password"
                    data-testid="perforce-credential"
                  />
                </div>
                <div className="col-md-6">
                  <Form.Label htmlFor="perforce-depot-path">Default depot path</Form.Label>
                  <Form.Control
                    id="perforce-depot-path"
                    value={connectionForm.default_depot_path}
                    onChange={(e) =>
                      setConnectionForm({
                        ...connectionForm,
                        default_depot_path: e.target.value,
                      })
                    }
                    placeholder="//Game/Main/..."
                    required
                    data-testid="perforce-depot-path"
                  />
                </div>
                <div className="col-md-6">
                  <Form.Label>Default stream (optional)</Form.Label>
                  <Form.Control
                    value={connectionForm.default_stream}
                    onChange={(e) =>
                      setConnectionForm({
                        ...connectionForm,
                        default_stream: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="col-md-4">
                  <Form.Label>Project subdirectory (optional)</Form.Label>
                  <Form.Control
                    value={connectionForm.project_root}
                    onChange={(e) =>
                      setConnectionForm({ ...connectionForm, project_root: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-4">
                  <Form.Label>Character set</Form.Label>
                  <Form.Control
                    value={connectionForm.charset}
                    onChange={(e) =>
                      setConnectionForm({ ...connectionForm, charset: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-4">
                  <Form.Label>SSL fingerprint (optional)</Form.Label>
                  <Form.Control
                    value={connectionForm.ssl_fingerprint}
                    onChange={(e) =>
                      setConnectionForm({
                        ...connectionForm,
                        ssl_fingerprint: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="col-12">
                  <Form.Label>Workspace strategy</Form.Label>
                  <Form.Control
                    value="Bakeonator-managed temporary workspace"
                    disabled
                    readOnly
                  />
                </div>
              </div>
              <div className="mt-3 d-flex gap-2">
                <Button type="submit" disabled={connectionBusy}>
                  {connectionBusy ? 'Saving…' : 'Save connection'}
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={() => {
                    setShowConnectionForm(false);
                    setConnectionForm(emptyConnectionForm);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          )}

          {connections.map((connection) => (
            <div
              key={connection.id}
              className="border rounded p-3 mb-3"
              data-testid={`connection-card-${connection.id}`}
            >
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <div>
                  <strong>{connection.display_name}</strong>{' '}
                  <StatusPill status={connection.status} />
                  <div className="small text-muted mono">{connection.server_address}</div>
                  <div className="small">
                    {connection.username} · {connection.authentication_method}
                  </div>
                  <div className="small mono">{connection.default_depot_path}</div>
                  <div className="small text-muted">
                    Credential{' '}
                    {connection.credential_configured
                      ? `configured (${connection.credential_hint || 'masked'})`
                      : 'missing'}
                    {connection.last_verified_at
                      ? ` · verified ${new Date(connection.last_verified_at).toLocaleString()}`
                      : ''}
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {canTestConnection && connection.status !== 'disabled' && (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      disabled={connectionBusy}
                      onClick={() => onTestConnection(connection.id)}
                      data-testid={`test-connection-${connection.id}`}
                    >
                      Test connection
                    </Button>
                  )}
                  {canRotateSecret && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() =>
                        setRotatingId(rotatingId === connection.id ? null : connection.id)
                      }
                      data-testid={`rotate-connection-${connection.id}`}
                    >
                      Rotate credential
                    </Button>
                  )}
                  {canUpdateConnection && connection.status !== 'disabled' && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={connectionBusy}
                      onClick={() => onDisableConnection(connection.id)}
                    >
                      Disable
                    </Button>
                  )}
                  {canDeleteConnection && (
                    <Button
                      size="sm"
                      variant="outline-danger"
                      disabled={connectionBusy}
                      onClick={() => onDeleteConnection(connection.id)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </div>
              {rotatingId === connection.id && canRotateSecret && (
                <Form
                  className="mt-3 row g-2 align-items-end"
                  onSubmit={(e) => {
                    e.preventDefault();
                    onRotateSecret(connection.id);
                  }}
                  autoComplete="off"
                >
                  <div className="col-md-8">
                    <Form.Label>New ticket or password</Form.Label>
                    <Form.Control
                      type="password"
                      value={rotateCredential}
                      onChange={(e) => setRotateCredential(e.target.value)}
                      required
                      autoComplete="new-password"
                      data-testid="rotate-credential-input"
                    />
                  </div>
                  <div className="col-md-4">
                    <Button type="submit" disabled={connectionBusy || !rotateCredential}>
                      Save credential
                    </Button>
                  </div>
                </Form>
              )}
            </div>
          ))}

          {canCreateConnection && connections.length > 0 && !showConnectionForm && (
            <Button
              variant="outline-primary"
              size="sm"
              className="mb-3"
              onClick={() => setShowConnectionForm(true)}
            >
              Add connection
            </Button>
          )}

          {canCreateFromSource && verifiedConnections.length > 0 && (
            <Form
              className="mt-2"
              onSubmit={onCreatePerforceRevision}
              data-testid="perforce-revision-form"
            >
              <h3 className="h6">Create revision</h3>
              <div className="row g-2">
                <div className="col-md-6">
                  <Form.Label>Connection</Form.Label>
                  <Form.Select
                    value={revisionForm.connection_id}
                    onChange={(e) => {
                      const id = e.target.value;
                      const selected = connections.find((c) => c.id === id);
                      setRevisionForm({
                        ...revisionForm,
                        connection_id: id,
                        depot_path: selected ? selected.default_depot_path : '',
                        project_root: selected ? selected.project_root || '' : '',
                      });
                    }}
                    required
                    data-testid="perforce-revision-connection"
                  >
                    {verifiedConnections.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.display_name}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-md-6">
                  <Form.Label>Depot path</Form.Label>
                  <Form.Control
                    value={revisionForm.depot_path}
                    onChange={(e) =>
                      setRevisionForm({ ...revisionForm, depot_path: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="col-md-4">
                  <Form.Label>Revision type</Form.Label>
                  <Form.Select
                    value={revisionForm.revision_type}
                    onChange={(e) =>
                      setRevisionForm({ ...revisionForm, revision_type: e.target.value })
                    }
                    data-testid="perforce-revision-type"
                  >
                    <option value="latest_submitted">Latest submitted</option>
                    <option value="changelist">Specific changelist</option>
                  </Form.Select>
                </div>
                {revisionForm.revision_type === 'changelist' && (
                  <div className="col-md-4">
                    <Form.Label>Changelist</Form.Label>
                    <Form.Control
                      value={revisionForm.changelist}
                      onChange={(e) =>
                        setRevisionForm({ ...revisionForm, changelist: e.target.value })
                      }
                      required
                      data-testid="perforce-changelist"
                    />
                  </div>
                )}
                <div className="col-md-4">
                  <Form.Label>Project subdirectory</Form.Label>
                  <Form.Control
                    value={revisionForm.project_root}
                    onChange={(e) =>
                      setRevisionForm({ ...revisionForm, project_root: e.target.value })
                    }
                  />
                </div>
              </div>
              <Form.Check
                className="mt-2"
                type="checkbox"
                id="clean-sync"
                label="Clean sync"
                checked
                disabled
                readOnly
              />
              <Button className="mt-3" type="submit" disabled={revisionBusy}>
                {revisionBusy ? 'Creating…' : 'Create revision'}
              </Button>
              {revisionMessage && (
                <p className="text-muted mt-2 mb-0">{revisionMessage}</p>
              )}
              {revisionError && <div className="error-state mt-2">{revisionError}</div>}
            </Form>
          )}
        </div>
      )}
    </div>
  );
}

export default ProjectRevisionsPanel;
